export type RetourOrder = {
  id: string;
  status: string;
  retour_date: string;
  comments: Comment[];
  products: RetourOrderProduct[];
  documents: Document[];
  history: RetourStatusChange[];
  order_id: number;
  shipping_id: string;
  license_plate: string;
  transporter: string;
  checked_user_name: string;
  customer_name: string;
  customer_code: string;
};

export type RetourStatusChange = {
  status: string;
  timestamp: string;
  user_id: string;
  user_name: string;
};

export type RetourOrderProduct = {
  amount: number;
  id: string;
  code: string;
  product_id: string;
  description: string;
  product_type: number;
  amount_packs: number;
  weight: number;
  pieces_per_pack: number;
  pieces_per_unit: number;
  unit: string;
  documents: Document[];
};

export enum DocumentType {
  Image = 'image/jpeg',
  PDF = 'application/pdf',
}

export type Document = {
  id: string;
  document: string;
  type: DocumentType;
};

export type User = {
  first_name: string;
  last_name: string;
  email: string;
};

export type PickupOrder = {
  id: string;
  status: number;
  pickup_date: string;
  being_loaded_time: string;
  ready_for_pickup_time: string;
  cleanup_time: string;
  init_user: string;
  loading_user: string;
  license_plate: string;
  transporter: string;
  has_return_products: boolean;
  sub_orders: PickupSubOrder[];
  comments: Comment[];
  history: StatusChange[];
};

export type ProductionUnit = {
  id: string;
  name: string;
  setting: ProductionUnitSetting;
};

export type ProductionUnitSetting = {
  id: string;
  amount: number;
  product_code: string;
  product_description: string;
};

export type StockCount = {
  id: string;
  status: string;
  product_code: string;
  product_name: string;
  product_pieces_per_pack: number;
  product_pieces_per_unit: number;
  product_weight: number;
  created_user: string;
  created_user_id: string;
  count: number;
  count_in_units: number;
  time: string;
  comments: Comment[];
  production_unit_name: string;
  automatic_stock_count?: AutomaticStockCountResponse;
};

export type AutomaticStockCountResponse = {
  id: string;
  time: string;
};

export enum AutomaticStockCountStatus {
  Unvalidated = 'unvalidated',
  Verified = 'verified',
  Forgotten = 'forgotten',
  Invalid = 'invalid',
}

export type AutomaticStockCount = {
  id: string;
  count: number;
  production_unit_id: string;
  status: AutomaticStockCountStatus;
  time: string;
  stock_count_id: string | null;
  last_reminder_at: string | null;
  reminder_count: number;
};

export type AutomaticStockCountUpdate = {
  status: AutomaticStockCountStatus.Invalid;
};

export type StockCountCreateResponse = {
  id: string;
};

export type SuccessResponse = {
  success: boolean;
};

export type Comment = {
  id: string;
  message: string;
  timestamp: string;
  user_id: string;
  user_name: string;
};

export type StatusChange = {
  status: number;
  timestamp: string;
  user_id: string;
  user_name: string;
};

export type PickupSubOrder = {
  id: string;
  order_id: number;
  shipping_id: string;
  external_id: string;
  products: Product[];
  shipping_city: string;
  customer_name: string;
};

export type Product = {
  amount: number;
  weight: number;
  id: string;
  code: string;
  description: string;
  amount_packs: number;
  product_type: number;
  ready_for_pickup: boolean;
  has_been_loaded: boolean;
};

export type PickupOrderList = PickupOrder[];

export function getOrderIDs(pickupOrder: PickupOrder) {
  const orderIDs: string[] = [];
  pickupOrder.sub_orders.forEach(val => {
    if (orderIDs.indexOf(val.order_id.toString()) === -1) {
      orderIDs.push(val.order_id.toString());
    }
  });

  return orderIDs.join(', ');
}

export function getCustomerNames(pickupOrder: PickupOrder) {
  const customerNames: string[] = [];
  pickupOrder.sub_orders.forEach(val => {
    if (customerNames.indexOf(val.customer_name.toString()) === -1) {
      customerNames.push(val.customer_name.toString());
    }
  });

  return customerNames.join(', ');
}

export function getShippingIDs(pickupOrder: PickupOrder) {
  const shippingIDs: string[] = [];
  pickupOrder.sub_orders.forEach(val => {
    if (shippingIDs.indexOf(val.shipping_id.toString()) === -1) {
      shippingIDs.push(val.shipping_id.toString());
    }
  });

  return shippingIDs.join(', ');
}

export function getShippingCities(pickupOrder: PickupOrder) {
  const shippingCities: string[] = [];
  pickupOrder.sub_orders.forEach(val => {
    shippingCities.push(val.shipping_city.toString());
  });

  return shippingCities.join(', ');
}

export type Claims = {
  sub: string;
};

export type ConfirmEvent = {
  title: string;
  description: string;
  confirm_event: string;
};
